import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import paths from "../../../utils/constants";
import { BreadCrumbSeparatorIcon, GiftBottomIcon, GiftIcon, GiftTopIcon } from "./Icons";
import api from "../../../api/api";
import { logMessage } from "../../../utils/helpers";
import { cn } from "../../../lib/utils";

const PURPLE = "#552F83";
const PAGE_BG = "#F1ECFF";

function isApiSuccess(res) {
  if (res == null) return false;
  const s = res.status;
  return s === 1 || s === "1" || Number(s) === 1;
}

function getStatusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "redeemed") return "text-[#552F83]";
  if (s === "available") return "text-[#00934C]";
  if (s === "expired") return "text-[#EF4444]";
  return "text-[#552F83]";
}

function getStatusBgColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "redeemed") return "bg-[#552F83]";
  if (s === "available") return "bg-[#00934C]";
  if (s === "expired") return "bg-[#EF4444]";
  return "bg-[#552F83]";
}

function getStatusBorderColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "redeemed") return "border-[#552F83]";
  if (s === "available") return "border-[#22C55E]";
  if (s === "expired") return "border-[#EF4444]";
  return "border-[#552F83]";
}

const RewardsPanel = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadersRef = useRef({ startLoading, stopLoading });
  loadersRef.current = { startLoading, stopLoading };

  const load = useCallback(async () => {
    const { startLoading: sl, stopLoading: el } = loadersRef.current;
    setLoading(true);
    sl?.();
    try {
      const res = await api.getRewards();

      let normalized = null;
      if (res && isApiSuccess(res) && res.data) {
        normalized = res.data;
      } else {
        logMessage(`getRewards non-success: ${res?.message || ""}`, "warn");
      }

      setPayload(normalized);
    } catch (e) {
      logMessage(e);
      setPayload(null);
    } finally {
      setLoading(false);
      el?.();
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const rewards = payload?.rewards || [];
  const totalRewards = Number(payload?.total ?? 0);
  const availableRewards = Number(payload?.available_rewards ?? 0);

  const handleViewDetails = (rewardId) => {
    navigate(paths.REWARD_DETAIL, { state: { userRewardId: rewardId } });
  };

  const handleStartProject = () => {
    navigate(paths.ADD_PROJECT);
  };

  return (
    <div className="relative min-h-full pb-10" style={{ backgroundColor: PAGE_BG }}>
        {rewards?.length === 0 ? (
          <div className="relative z-10 px-4 pt-4">
            <p className="text-[12px] drop-shadow-sm flex items-center flex-wrap gap-0">
              <button
                type="button"
                className="cursor-pointer hover:underline text-[#323232] bg-transparent border-0 p-0 font-inherit"
                onClick={() => navigate(paths.YOUR_PROGRESS)}
              >
                Your progress
              </button>
              <BreadCrumbSeparatorIcon className="px-1.5 size-2.5 shrink-0 text-[#323232]" />
              <span className="font-medium text-[#323232]">My Rewards</span>
            </p>
          </div>
        ) : (
          <div
            className="relative overflow-hidden pb-8"
            style={{
              backgroundImage: "url('/bgImage.webp')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="relative z-10 px-4 pt-4">
              <p className="text-[12px] text-white drop-shadow-sm flex items-center flex-wrap gap-0">
                <button
                  type="button"
                  className="cursor-pointer hover:underline text-white bg-transparent border-0 p-0 font-inherit"
                  onClick={() => navigate(paths.YOUR_PROGRESS)}
                >
                  Your progress
                </button>
                <BreadCrumbSeparatorIcon className="px-1.5 size-2.5 shrink-0" />
                <span className="font-medium text-white">My Rewards</span>
              </p>
            </div>
            <div className="min-w-0 flex-1 flex items-center justify-center gap-4">
                <GiftIcon width={120} height={130}/>
                <div className=" flex flex-col gap-3 text-center sm:text-left">
                  <span
                    className="text-[26px] md:text-[32px] font-bold leading-[120%] text-[#FFFFFF]"
                  >
                    {String(availableRewards).padStart(2, "0")}
                  </span>
                  <span className="text-sm md:text-base text-[#FFFFFF] font-semibold leading-[170%] tracking-[2%]">
                      AVAILABLE REWARDS
                  </span>
                </div>
            </div>
          </div>
        )}

      <div className="relative z-20 px-4">
        <div className="mx-auto max-w-[90%] flex flex-col gap-6 mt-6">
          {loading ? (
            <p className="text-center text-sm text-[#7C7790] py-8">Loading rewards…</p>
          ) : !payload ? (
            <p className="text-center text-sm text-[#7C7790] py-8">Could not load rewards. Please try again.</p>
          ) : rewards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              <div className="relative flex flex-col items-center justify-center gap-4 mb-6 bg-white rounded-full size-[250px]">
                <div className="absolute top-[-50px]">
                  <GiftTopIcon />
                </div>
                <span className="min-w-[300px] text-xl font-bold text-[#323232] mb-0 text-center">No Available Rewards</span>
                <span className="min-w-[300px] text-sm text-[#7C7790] mb-14 text-center max-w-md">
                    Continue painting consistently to become eligible for rewards. 
                </span>
                <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
                    <div className="absolute bottom-0 left-10 z-10">
                        <GiftBottomIcon />
                    </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleStartProject}
                className="px-16 py-3 rounded-[8px] text-white font-semibold shadow-md hover:shadow-lg transition-shadow bg-[#552F83] border-0"
              >
                Start a Project
              </button>
            </div>
          ) : (
            /* ========================================================= */
            /* RE-ARCHITECTED GRID VIEW WRAPPER                           */
            /* ========================================================= */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16 pt-16 mx-auto w-full items-start">
              {rewards.map((reward) => (
                <div
                 key={reward._id}
  /* CHANGED: 
     - Added 'box-border' to force padding and borders into the width math.
     - Added 'mx-auto' to ensure the card stays micro-centered within its column split.
  */
  className="relative w-[calc(100%-2px)] mx-auto box-border col-span-1 rounded-[18px] bg-white p-5 border flex flex-col gap-4 z-0"
  style={{
    boxShadow: "0px 0px 4px 0px #0000001A",
  }}
                >
                  {/* STRUCTURAL HEADER CONTAINER */}
                  <div className="relative flex flex-col items-center w-full pt-6">
                    
                    {/* Status Badge Tracker */}
                    <div className="absolute right-0 top-[-8px]">
                      <span
                        className={cn(
                          "text-xs font-semibold px-3 py-1 rounded-full capitalize",
                          getStatusBgColor(reward.status),
                          "text-white"
                        )}
                      >
                        {reward.status}
                      </span>
                    </div>

                    {/* Purple Circle Icon */}
                    <div
                      className="absolute  top-[-56px] w-16 h-16 rounded-full flex items-center justify-center shrink-0 shadow-sm"
                      style={{ backgroundColor: "#552F83" }}
                    >
                      {reward.main_icon ? (
                        <img
                          src={reward.main_icon}
                          alt={reward.name}
                          className="w-10 h-10 object-contain"
                        />
                      ) : (
                        <svg width="40" height="40" viewBox="0 0 94 94" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.9737 23.1621C11.9737 23.1621 7.26502 23.5344 6.6372 24.5929C6.00208 25.6515 5.96558 27.2794 5.96558 29.068C5.96558 30.8638 5.89258 34.9811 5.89258 34.9811L7.01681 37.3975C7.01681 37.3975 7.22852 68.1971 7.22852 69.1534C7.22852 70.1025 7.22852 71.2632 8.60096 72.0005C9.9734 72.7378 44.8538 89.5867 45.9415 90.0393C47.5841 90.7183 48.3652 90.4627 48.3652 90.4627L87.122 25.6442C87.122 25.6442 86.9979 23.8702 85.3846 23.4468C84.4574 23.2132 11.9737 23.1621 11.9737 23.1621Z" fill="#F5B03E"/>
                        </svg>
                      )}
                    </div>

                    {/* Card Typography Headers */}
                    <div className="flex flex-col items-center justify-center text-center mt-4 w-full">
                      <h3 className="font-semibold text-[#323232] text-base leading-tight mb-1">
                        {reward.name}
                      </h3>
                      {reward.category_text && (
                        <p className="text-xs text-[#7C7790]">{reward.category_text}</p>
                      )}
                    </div>
                  </div>

                  {/* Button Action Footer Block */}
                  <div className="flex items-center justify-center pb-2 mt-auto pt-2">
                    <button
                      type="button"
                      onClick={() => handleViewDetails(reward._id)}
                      className={cn(
                        "text-xs bg-[#F1ECFF] text-[#523A92] font-semibold py-2 rounded-[10px] border transition-colors border-[#552F83] px-12",
                        "hover:opacity-80"
                      )}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardsPanel;
